import { NgFor, NgIf } from '@angular/common';
import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { OfferItem, OfferLineAggregation } from '../../models/offer';
import { AppOfferService } from '../../services/app-offer.service';
import { AppWizardService } from '../../services/app-wizard.service';
import { OfferFormatService } from '../../services/offer-format.service';
import { OfferService } from '../../services/offer.api.service';

import { OfferRequestWidgetComponent } from './offer-request-widget.component';

const STATUS_OFFER: readonly string[] = ['REQUESTED', 'OFFERED', 'ACCEPTED'];

interface OfferLineViewModel {
	suppOffRef?: string;
	itemNumber?: string;
	standardGrade?: string;
	period?: string;
	fullPeriod?: string;
	dimension?: string;
	quantity?: string;
	currency?: string;
	opportunityID?: string;
}

@Component({
	selector: 'copo-offer-dashboard',
	templateUrl: './offer-dashboard.component.html',
	styleUrl: './offer-dashboard.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		NgFor,
		TranslateModule,
		MatRippleModule,
		MatButtonModule,
		PageHeaderComponent,
		LoadingSpinnerComponent,
		ScrollerDirective,
		NumberPipe,
		OfferRequestWidgetComponent,
		MatButton,
		RouterModule,
		MatTooltipModule,
	]
})
export class OfferDashboardComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	aggregations: OfferLineAggregation[] = [];
	offerLinesStream: OfferLineViewModel[] = [];
	readonly UNDERSCORE = UNDERSCORE;
	isLoading = true;
	isProposal = true;
	status: 'loading' | 'success' | 'error' = 'loading';

	private dashboardSubscription?: Subscription;
	private lastProposalSubscription?: Subscription;
	private readonly DEFAULT_OFFER_FIELDS: (keyof OfferItem)[] = [
		'opportunityID',
		'suppOffRef',
		'itemNumber',
		'standardSteelGrade',
		'productDimension',
		'periodStart',
		'periodEnd',
		'priceUnit',
		'quantityValue',
		'productCurrency'
	];

	constructor(
		private readonly router: Router,
		private readonly offerService: OfferService,
		private readonly appOfferService: AppOfferService,
		private readonly appWizardService: AppWizardService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly format: OfferFormatService
	) {}

	get user() {
		return this.userService.user;
	}

	ngOnInit(): void {
		this.loadDashboard();
		this.loadLastProposals();
	}

	ngOnDestroy(): void {
		this.dashboardSubscription?.unsubscribe();
		this.lastProposalSubscription?.unsubscribe();
	}

	trackById(_index: number, item: OfferItem): string {
		return item.opportunityID;
	}

	async evtGoToOfferList(aggregation: OfferLineAggregation): Promise<void> {
		await this.router.navigateByUrl(`offer/list/${ aggregation.status.toLowerCase() }`);
	}

	async evtNewOffer(): Promise<void> {
		const frameworkEnabled = this.userService.user.hasServiceParameterEqualsTo(
			'EOFFR',
			'offerFrameworkOpen',
			'True'
		);
		sessionStorage.setItem('OFFER_TYPE', frameworkEnabled ? 'framework' : 'spot');
		await this.appWizardService.actionNewOffer();
	}

	private loadDashboard(): void {
		this.dashboardSubscription = this.offerService.getOfferDashboardResponse().subscribe({
			next: response => {
				this.aggregations = response.offerAggregations
					.filter(a => STATUS_OFFER.includes(a.status))
					.sort((a1, a2) => STATUS_OFFER.indexOf(a1.status) - STATUS_OFFER.indexOf(a2.status));
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error('Unable to retrieve offer dashboard data', err);
				this.aggregations = [];
				this.isLoading = false;
				this.aggregations = [];
				this.appOfferService.showError(this.translationService.getTR('eoffer.offer.dashboard.not.retrived'));
			}
		});
	}

	private loadLastProposals(
		fields: (keyof OfferItem)[] = this.DEFAULT_OFFER_FIELDS,
		filter = 'status:OFFERED',
		limit = 5
	): void {
		this.lastProposalSubscription?.unsubscribe();
		this.status = 'loading';
		this.lastProposalSubscription = this.offerService.getOffers(
			fields,
			filter,
			0,
			limit,
			[new OrderBy('lastModifiedDate', OrderByDirection.DESC)],
			false
		).subscribe({
			next: response => {
				this.offerLinesStream = response.results.map(this.mapToViewModel.bind(this));
				this.status = 'success';
			},
			error: (err: unknown) => {
				console.error('Unable to retrieve offer lines data', err);
				this.offerLinesStream = [];
				this.status = 'error';
				this.appOfferService.showError(
					this.translationService.getTR('offers.proposals.price.title')
				);
			}
		});
	}

	private mapToViewModel(item: OfferItem): OfferLineViewModel {
		return {
			opportunityID: item.opportunityID,
			suppOffRef: item.suppOffRef,
			itemNumber: item.itemNumber,
			standardGrade: item.standardSteelGrade,
			period: this.format.formatPeriod(item.periodStart, item.periodEnd),
			fullPeriod: `${ item.periodStart } / ${ item.periodEnd }`,
			dimension: this.format.formatDimension(item.productDimension),
			quantity: this.format.formatPrice(item.quantityValue, item.priceUnit),
		};
	}
}
